import { useEffect, useState } from "react";
import { toast } from "sonner";
import { ShieldAlert, Loader2, Save, Unlock } from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { Button } from "../ui/button";
import { Input } from "../ui/input";

const FIELDS = [
  { key: "login_max_attempts", label: "Tentatives avant blocage", hint: "par e-mail et par adresse IP (3–50)", min: 3, max: 50 },
  { key: "lock_minutes", label: "Durée du blocage (minutes)", hint: "1–1440", min: 1, max: 1440 },
  { key: "password_min_length", label: "Longueur minimale du mot de passe", hint: "8–64 (majuscule, chiffre et symbole exigés)", min: 8, max: 64 },
];

export default function LoginProtectionCard() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [cfg, setCfg] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => api.get("/auth/security-settings").then((r) => setCfg(r.data)).catch(() => {});
  useEffect(() => { load(); }, []); // eslint-disable-line

  const save = async () => {
    setBusy(true);
    try {
      const r = await api.put("/auth/security-settings", { ...Object.fromEntries(FIELDS.map((f) => [f.key, Number(cfg[f.key])])), twofa_required_roles: cfg.twofa_required_roles || [], twofa_trusted_days: Number(cfg.twofa_trusted_days ?? 30) });
      setCfg((c) => ({ ...c, ...r.data }));
      toast.success("Protection des connexions enregistrée");
    } catch (e) { toast.error(e.response?.data?.detail || "Enregistrement impossible"); } finally { setBusy(false); }
  };
  const unlockAll = async () => {
    try { const r = await api.delete("/auth/security-settings/locks"); toast.success(`${r.data.cleared} blocage(s) levé(s)`); load(); } catch { toast.error("Action impossible"); }
  };

  if (!cfg) return null;
  return (
    <div className="bg-[#14101c] border border-white/5 rounded-xl p-5 space-y-4" data-testid="login-protection-card">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <ShieldAlert className="w-4 h-4 text-[#f7c935]" />
          <h3 className="text-sm font-bold text-white">Connexion — anti brute-force & mots de passe</h3>
        </div>
        <span className="text-[11px] text-slate-400" data-testid="login-protection-locked">{cfg.currently_locked || 0} blocage(s) actif(s)</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        {FIELDS.map((f) => (
          <label key={f.key} className="text-xs text-slate-300 space-y-1">
            <span className="font-bold">{f.label}</span>
            <Input type="number" min={f.min} max={f.max} value={cfg[f.key]} onChange={(e) => setCfg({ ...cfg, [f.key]: e.target.value })}
              className="bg-[#0b0811] border-white/10 text-white h-9" data-testid={`login-protection-${f.key}`} />
            <span className="text-[10px] text-slate-500">{f.hint}</span>
          </label>
        ))}
      </div>
      <div className="border-t border-white/5 pt-3" data-testid="twofa-policy">
        <div className="text-xs font-bold text-slate-300 mb-2">Double authentification obligatoire pour :</div>
        <div className="flex flex-wrap gap-2">
          {[["superadmin", "Master admin"], ["admin", "Staff"], ["tenant_admin", "Admin société"], ["tenant_member", "Salariés"], ["formateur", "Formateurs"], ["partner", "Partenaires"]].map(([k, l]) => {
            const on = (cfg.twofa_required_roles || []).includes(k);
            return (
              <button key={k} type="button" onClick={() => setCfg({ ...cfg, twofa_required_roles: on ? cfg.twofa_required_roles.filter((r) => r !== k) : [...(cfg.twofa_required_roles || []), k] })}
                className={`text-xs px-2.5 py-1 rounded-full border ${on ? "border-[#f7c935] text-[#f7c935] bg-[#f7c935]/10" : "border-white/10 text-slate-400"}`} data-testid={`twofa-role-${k}`}>{l}</button>
            );
          })}
        </div>
        <label className="text-xs text-slate-300 mt-3 flex items-center gap-2">
          <span className="font-bold">Appareil de confiance (jours sans redemander le code)</span>
          <Input type="number" min={0} max={365} value={cfg.twofa_trusted_days ?? 30} onChange={(e) => setCfg({ ...cfg, twofa_trusted_days: e.target.value })} className="bg-[#0b0811] border-white/10 text-white h-8 w-20" data-testid="twofa-trusted-days" />
        </label>
        <p className="text-[10px] text-slate-500 mt-1">Les utilisateurs concernés doivent configurer la 2FA (application ou e-mail) à leur prochaine connexion. Journal : <a href="/admin/security/audit" className="text-[#f7c935] hover:underline" data-testid="link-access-audit">Audit des accès</a>.</p>
      </div>
      <p className="text-[11px] text-slate-500">S'applique à la connexion et à la réinitialisation de mot de passe. Un mot de passe par défaut (ex. admin123) déclenche un changement obligatoire à la connexion.</p>
      <div className="flex gap-2 justify-end">
        <Button size="sm" variant="ghost" onClick={unlockAll} className="text-xs text-slate-300 border border-white/10" data-testid="login-protection-unlock-btn"><Unlock className="w-3.5 h-3.5 mr-1" /> Lever les blocages</Button>
        <Button size="sm" onClick={save} disabled={busy} className="bg-[#f7c935] text-[#0b0811] hover:bg-[#e6b825] text-xs font-bold" data-testid="login-protection-save-btn">
          {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1" /> Enregistrer</>}
        </Button>
      </div>
    </div>
  );
}
